import React from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import SelfAssessmentQuiz from '../components/SelfAssessmentQuiz';
import MentalHealthResources from '../components/MentalHealthResources';
import InteractiveFAQ from '../components/InteractiveFAQ';

const ResourcesPage = () => {
    const { t } = useTranslation();

    return (
        <>
            <SEO 
                title={t('resources_page.seo_title') || "Resurse Gratuite de Sănătate Mentală - Oana Tenea"}
                description={t('resources_page.seo_desc') || "Accesează teste de autoevaluare pentru anxietate, ghiduri practice de sănătate mentală, cărți recomandate și numere de urgență oferite de psihoterapeutul Oana Tenea."}
                path="/resurse"
            />
            
            <div className="bg-stone-50 dark:bg-stone-950 text-stone-800 dark:text-stone-200 transition-colors duration-300">
                
                {/* Hero Section */}
                <section className="py-20 sm:py-24 bg-gradient-to-b from-stone-100/90 via-warm-beige/40 to-stone-50 dark:from-stone-900/90 dark:via-stone-900/40 dark:to-stone-950 relative overflow-hidden">
                    <div className="absolute top-0 right-1/4 w-96 h-96 bg-terracotta/10 rounded-full blur-3xl pointer-events-none"></div>

                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 max-w-5xl">
                        <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300 text-xs font-semibold tracking-wider uppercase mb-4 border border-stone-200 dark:border-stone-700 shadow-xs">
                            Autocunoaștere & Suport
                        </span>

                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 dark:text-stone-100 tracking-tight mb-6">
                            {t('resources_page.title1')}{' '}
                            <span className="italic block sm:inline font-normal font-display text-terracotta">
                                {t('resources_page.title2')}
                            </span>
                        </h1>

                        <p className="text-stone-600 dark:text-stone-300 text-base sm:text-lg lg:text-xl mb-12 max-w-3xl mx-auto leading-relaxed">
                            {t('resources_page.subtitle')}
                        </p>
                        
                        {/* 3 Value Pillars */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto text-left">
                            <div className="bg-white/80 dark:bg-stone-900/80 backdrop-blur-md rounded-3xl p-6 border border-stone-200/80 dark:border-stone-800 shadow-soft">
                                <div className="w-10 h-10 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-sm mb-4">
                                    01
                                </div>
                                <h3 className="font-display text-lg font-bold text-stone-900 dark:text-stone-100 mb-2">
                                    {t('resources_page.feat1_title')}
                                </h3>
                                <p className="text-stone-500 dark:text-stone-400 text-xs sm:text-sm leading-relaxed">
                                    {t('resources_page.feat1_desc')}
                                </p>
                            </div>

                            <div className="bg-white/80 dark:bg-stone-900/80 backdrop-blur-md rounded-3xl p-6 border border-stone-200/80 dark:border-stone-800 shadow-soft">
                                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-700 dark:text-amber-400 flex items-center justify-center font-bold text-sm mb-4">
                                    02
                                </div>
                                <h3 className="font-display text-lg font-bold text-stone-900 dark:text-stone-100 mb-2">
                                    {t('resources_page.feat2_title')}
                                </h3>
                                <p className="text-stone-500 dark:text-stone-400 text-xs sm:text-sm leading-relaxed">
                                    {t('resources_page.feat2_desc')}
                                </p>
                            </div>

                            <div className="bg-white/80 dark:bg-stone-900/80 backdrop-blur-md rounded-3xl p-6 border border-stone-200/80 dark:border-stone-800 shadow-soft">
                                <div className="w-10 h-10 rounded-xl bg-sage-500/10 text-sage-700 dark:text-sage-300 flex items-center justify-center font-bold text-sm mb-4">
                                    03
                                </div>
                                <h3 className="font-display text-lg font-bold text-stone-900 dark:text-stone-100 mb-2">
                                    {t('resources_page.feat3_title')}
                                </h3>
                                <p className="text-stone-500 dark:text-stone-400 text-xs sm:text-sm leading-relaxed">
                                    {t('resources_page.feat3_desc')}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Subnav Anchor Bar */}
                <section className="py-4 bg-white/95 dark:bg-stone-900/95 backdrop-blur-md border-y border-stone-200/80 dark:border-stone-800 sticky top-20 z-40">
                    <div className="container mx-auto px-6">
                        <div className="flex flex-wrap justify-center gap-3">
                            <a 
                                href="#self-assessment" 
                                className="px-5 py-2 rounded-full text-xs font-semibold bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-terracotta hover:text-white transition-colors"
                            >
                                {t('resources_page.nav1')}
                            </a>
                            <a 
                                href="#resources" 
                                className="px-5 py-2 rounded-full text-xs font-semibold bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-terracotta hover:text-white transition-colors"
                            >
                                {t('resources_page.nav2')}
                            </a>
                            <a 
                                href="#faq" 
                                className="px-5 py-2 rounded-full text-xs font-semibold bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 hover:bg-terracotta hover:text-white transition-colors"
                            >
                                {t('resources_page.nav3')}
                            </a>
                        </div>
                    </div>
                </section>

                {/* Self Assessment Section */}
                <div id="self-assessment" className="py-20 sm:py-24 border-b border-stone-200/70 dark:border-stone-800">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12 max-w-xl mx-auto">
                            <span className="text-xs font-semibold uppercase tracking-wider text-terracotta mb-2 block">
                                Test Interactiv
                            </span>
                            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 dark:text-stone-100">
                                {t('resources_page.nav1')}
                            </h2>
                        </div>
                        <SelfAssessmentQuiz />
                    </div>
                </div>

                {/* Mental Health Resources Section */}
                <div id="resources" className="py-20 sm:py-24 border-b border-stone-200/70 dark:border-stone-800">
                    <MentalHealthResources />
                </div>

                {/* Interactive FAQ Section */}
                <div id="faq" className="py-20 sm:py-24">
                    <InteractiveFAQ />
                </div>

            </div>
        </>
    );
};

export default ResourcesPage;
